import React, { Component } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faAdd, faTrash, faUpload, faImage, faRefresh, faGripVertical, faBook, faFile, faFilePdf, faDownload } from '@fortawesome/free-solid-svg-icons';
import SwalServices from '../../services/swalServices/SwalServices';
import AdminServices from '../../services/axiosServices/apiServices/adminServices';
import { ProgressSpinner } from 'primereact/progressspinner';
import { APIURL } from '../../services/axiosServices/ApiEndPoints';
import { CommonSuccessMessages } from '../../utils/Messages';

export default class Configuration extends Component {
    constructor(props) {
        super(props);
        this.swalServices = new SwalServices();
        this.adminServices = new AdminServices();
        this.state = {
            // CPD Images
            isLoading: false,
            isUploading: false,
            isSaving: false,
            isReordering: false,
            tempImages: [],
            savedImages: [],
            updateImageId: null,
            draggedIndex: null,
            // Handbooks
            casualHandbook: null,
            permanentHandbook: null,
            isCasualLoading: false,
            isPermanentLoading: false,
            isCasualUploading: false,
            isPermanentUploading: false,
        };
        this.fileInputRef = React.createRef();
        this.casualFileRef = React.createRef();
        this.permanentFileRef = React.createRef();
    }

    componentDidMount() {
        this.getCPDImages();
        this.getHandbook('casual');
        this.getHandbook('permanent');
    }

    // ==================== CPD Images ====================
    getCPDImages = () => {
        this.setState({ isLoading: true });
        this.adminServices.getCPDImages().then((response) => {
            if (response?.success === 1) {
                this.setState({ savedImages: response.data.images || [] });
            }
            this.setState({ isLoading: false });
        }).catch(() => this.setState({ isLoading: false }));
    };

    handleFileSelect = async (event) => {
        const files = Array.from(event.target.files);
        if (files.length === 0) return;

        const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif'];
        const maxSize = 2 * 1024 * 1024;

        for (const file of files) {
            if (!allowedTypes.includes(file.type)) {
                this.swalServices.Error(`Invalid file type: ${file.name}`);
                return;
            }
            if (file.size > maxSize) {
                this.swalServices.Error(`File too large: ${file.name}`);
                return;
            }
        }

        this.setState({ isUploading: true });

        const uploadPromises = files.map(async (file) => {
            const formData = new FormData();
            formData.append('image', file);
            try {
                const response = await this.adminServices.uploadTempImage(formData);
                if (response?.success === 1) {
                    return { tempPath: response.data.tempPath, filename: response.data.filename, originalName: response.data.originalName, preview: URL.createObjectURL(file) };
                }
            } catch (error) { }
            return null;
        });

        const results = await Promise.all(uploadPromises);
        const successfulUploads = results.filter(r => r !== null);

        if (successfulUploads.length > 0) {
            this.setState(prevState => ({ tempImages: [...prevState.tempImages, ...successfulUploads] }));
        }

        this.setState({ isUploading: false });
        if (this.fileInputRef.current) this.fileInputRef.current.value = '';
    };

    removeTempImage = (index) => {
        this.setState(prevState => {
            const newTempImages = [...prevState.tempImages];
            if (newTempImages[index].preview) URL.revokeObjectURL(newTempImages[index].preview);
            newTempImages.splice(index, 1);
            return { tempImages: newTempImages };
        });
    };

    saveImages = () => {
        if (this.state.tempImages.length === 0) return;

        this.setState({ isSaving: true });
        const images = this.state.tempImages.map(img => ({ tempPath: img.tempPath, filename: img.filename }));

        this.adminServices.saveCPDImages({ images }).then((response) => {
            if (response?.success === 1) {
                this.state.tempImages.forEach(img => { if (img.preview) URL.revokeObjectURL(img.preview); });
                this.setState({ tempImages: [] });
                this.swalServices.Success(response.msg);
                this.getCPDImages();
            } else {
                this.swalServices.Error(response?.msg || 'Failed to save');
            }
            this.setState({ isSaving: false });
        }).catch(() => this.setState({ isSaving: false }));
    };

    deleteImage = (filename) => {
        this.swalServices.Confirm("", "", CommonSuccessMessages.Yes_Text, CommonSuccessMessages.No_Text, "Delete this image?").then((confirmed) => {
            if (confirmed) {
                this.adminServices.deleteCPDImage({ filename }).then((response) => {
                    if (response?.success === 1) {
                        this.swalServices.Success(response.msg);
                        this.getCPDImages();
                    }
                });
            }
        });
    };

    handleUpdateFileSelect = async (event, oldFilename) => {
        const file = event.target.files[0];
        if (!file) return;

        const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif'];
        if (!allowedTypes.includes(file.type) || file.size > 2 * 1024 * 1024) {
            this.swalServices.Error('Invalid file');
            return;
        }

        this.setState({ updateImageId: oldFilename });
        const formData = new FormData();
        formData.append('image', file);
        formData.append('oldFilename', oldFilename);

        this.adminServices.updateCPDImage(formData).then((response) => {
            if (response?.success === 1) {
                this.swalServices.Success(response.msg);
                this.getCPDImages();
            }
            this.setState({ updateImageId: null });
        }).catch(() => this.setState({ updateImageId: null }));

        event.target.value = '';
    };

    handleDragStart = (e, index) => {
        this.setState({ draggedIndex: index });
        e.dataTransfer.effectAllowed = 'move';
    };

    handleDragOver = (e, index) => {
        e.preventDefault();
        const { draggedIndex, savedImages } = this.state;
        if (draggedIndex === null || draggedIndex === index) return;

        const newImages = [...savedImages];
        const draggedItem = newImages[draggedIndex];
        newImages.splice(draggedIndex, 1);
        newImages.splice(index, 0, draggedItem);

        this.setState({ savedImages: newImages, draggedIndex: index });
    };

    handleDragEnd = () => {
        const { draggedIndex } = this.state;
        if (draggedIndex !== null) {
            this.saveOrder();
        }
        this.setState({ draggedIndex: null });
    };

    saveOrder = () => {
        this.setState({ isReordering: true });
        const order = this.state.savedImages.map(img => img.filename);

        this.adminServices.updateImageOrder({ order }).then((response) => {
            if (response?.success === 1) {
                this.getCPDImages();
            }
            this.setState({ isReordering: false });
        }).catch(() => this.setState({ isReordering: false }));
    };

    // ==================== Handbooks ====================
    getHandbook = (type) => {
        const loadingKey = type === 'casual' ? 'isCasualLoading' : 'isPermanentLoading';
        const dataKey = type === 'casual' ? 'casualHandbook' : 'permanentHandbook';
        this.setState({ [loadingKey]: true });
        this.adminServices.getHandbook(type).then((response) => {
            if (response?.success === 1) {
                this.setState({ [dataKey]: response.data.file });
            }
            this.setState({ [loadingKey]: false });
        }).catch(() => this.setState({ [loadingKey]: false }));
    };

    handleHandbookUpload = async (event, type) => {
        const file = event.target.files[0];
        if (!file) return;

        const allowedExts = ['.pdf', '.doc', '.docx'];
        const ext = file.name.substring(file.name.lastIndexOf('.')).toLowerCase();
        if (!allowedExts.includes(ext)) {
            this.swalServices.Error('Only PDF, DOC, DOCX files are allowed');
            return;
        }
        if (file.size > 20 * 1024 * 1024) {
            this.swalServices.Error('File size must be under 20MB');
            return;
        }

        const uploadingKey = type === 'casual' ? 'isCasualUploading' : 'isPermanentUploading';
        this.setState({ [uploadingKey]: true });

        const formData = new FormData();
        formData.append('handbook', file);
        formData.append('type', type);

        this.adminServices.uploadHandbook(formData).then((response) => {
            if (response?.success === 1) {
                this.swalServices.Success(response.msg);
                this.getHandbook(type);
            } else {
                this.swalServices.Error(response?.msg || 'Upload failed');
            }
            this.setState({ [uploadingKey]: false });
        }).catch(() => this.setState({ [uploadingKey]: false }));

        event.target.value = '';
    };

    deleteHandbook = (type) => {
        const label = type === 'casual' ? 'Casual' : 'Permanent';
        this.swalServices.Confirm("", "", CommonSuccessMessages.Yes_Text, CommonSuccessMessages.No_Text, `Delete ${label} Employee Handbook?`).then((confirmed) => {
            if (confirmed) {
                this.adminServices.deleteHandbook({ type }).then((response) => {
                    if (response?.success === 1) {
                        this.swalServices.Success(response.msg);
                        this.getHandbook(type);
                    }
                });
            }
        });
    };

    formatFileSize = (bytes) => {
        if (!bytes) return '0 B';
        const sizes = ['B', 'KB', 'MB', 'GB'];
        const i = Math.floor(Math.log(bytes) / Math.log(1024));
        return (bytes / Math.pow(1024, i)).toFixed(1) + ' ' + sizes[i];
    };

    renderHandbookSection = (type, label, handbookData, isLoading, isUploading, fileRef) => {
        return (
            <div className='p-5 bg-white rounded-[16px]' style={{ border: "1px solid #DFDFDF" }}>
                <div className='flex items-center gap-3 mb-4'>
                    <div className='w-[40px] h-[40px] rounded-[10px] flex items-center justify-center' style={{ backgroundColor: type === 'casual' ? '#EFF6FF' : '#F0FDF4' }}>
                        <FontAwesomeIcon icon={faBook} className={type === 'casual' ? 'text-blue-600' : 'text-green-600'} />
                    </div>
                    <div>
                        <h2 className='text-[16px] font-semibold m-0'>{label} Employee Handbook</h2>
                        <p className='text-[12px] text-gray-500 m-0'>Upload or replace the {label.toLowerCase()} employee handbook file</p>
                    </div>
                </div>

                {isLoading ? (
                    <div className='flex justify-center py-6'>
                        <ProgressSpinner style={{ width: '30px', height: '30px' }} strokeWidth="4" />
                    </div>
                ) : handbookData ? (
                    <div className='flex items-center justify-between p-4 bg-gray-50 rounded-[12px] border border-gray-200'>
                        <div className='flex items-center gap-3'>
                            <div className='w-[44px] h-[44px] rounded-[10px] bg-red-50 flex items-center justify-center'>
                                <FontAwesomeIcon icon={faFilePdf} className='text-red-500 text-[20px]' />
                            </div>
                            <div>
                                <p className='m-0 text-[14px] font-medium text-gray-800' style={{ wordBreak: 'break-all' }}>{handbookData.filename}</p>
                                <p className='m-0 text-[12px] text-gray-500'>
                                    {this.formatFileSize(handbookData.size)} &bull; Uploaded: {new Date(handbookData.uploadedAt).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' })}
                                </p>
                            </div>
                        </div>
                        <div className='flex items-center gap-2'>
                            <label className='py-[8px] px-[14px] text-[13px] font-medium rounded-[8px] text-white bg-[#004AAD] hover:bg-[#003080] cursor-pointer border-0'>
                                <FontAwesomeIcon icon={faUpload} className='mr-1' /> Replace
                                <input
                                    type='file'
                                    accept='.pdf,.doc,.docx'
                                    onChange={(e) => this.handleHandbookUpload(e, type)}
                                    className='hidden'
                                    ref={fileRef}
                                />
                            </label>
                            <button onClick={() => this.deleteHandbook(type)} className='py-[8px] px-[14px] text-[13px] font-medium rounded-[8px] text-red-600 bg-red-50 hover:bg-red-100 border-0 cursor-pointer'>
                                <FontAwesomeIcon icon={faTrash} />
                            </button>
                        </div>
                    </div>
                ) : (
                    <div className='text-center py-8 border-2 border-dashed border-gray-300 rounded-[12px] bg-gray-50'>
                        <FontAwesomeIcon icon={faFile} className='text-3xl text-gray-400 mb-3' />
                        <p className='text-gray-500 m-0 mb-3'>No handbook uploaded yet</p>
                        <label className='inline-flex items-center gap-[8px] py-[10px] px-[20px] text-[14px] font-medium rounded-[8px] text-white bg-[#004AAD] hover:bg-[#003080] cursor-pointer border-0'>
                            {isUploading ? (
                                <><ProgressSpinner style={{ width: '16px', height: '16px' }} strokeWidth="4" /> Uploading...</>
                            ) : (
                                <><FontAwesomeIcon icon={faUpload} /> Upload Handbook</>
                            )}
                            <input
                                type='file'
                                accept='.pdf,.doc,.docx'
                                onChange={(e) => this.handleHandbookUpload(e, type)}
                                className='hidden'
                                ref={fileRef}
                                disabled={isUploading}
                            />
                        </label>
                        <p className='text-[12px] text-gray-400 m-0 mt-2'>Allowed: PDF, DOC, DOCX (Max 20MB)</p>
                    </div>
                )}

                {isUploading && handbookData && (
                    <div className='flex items-center gap-2 mt-3 text-blue-600 text-sm'>
                        <ProgressSpinner style={{ width: '16px', height: '16px' }} strokeWidth="4" /> Uploading...
                    </div>
                )}
            </div>
        );
    };

    render() {
        return (
            <div className='configuration-page' style={{ height: "calc((100dvh / 0.85) - 28.8px)" }}>
                <div className='configuration-inner h-full p-[30px] overflow-y-auto'>

                    {/* ==================== Section 1: Handbook Upload ==================== */}
                    <div className='mb-[30px]'>
                        <div className='flex items-center gap-2 mb-[15px]'>
                            <h1 className='text-[20px] m-0 font-semibold text-[#1e293b]'>Handbook Management</h1>
                        </div>
                        <div className='grid grid-cols-1 lg:grid-cols-2 gap-[20px]'>
                            {this.renderHandbookSection('casual', 'Casual', this.state.casualHandbook, this.state.isCasualLoading, this.state.isCasualUploading, this.casualFileRef)}
                            {this.renderHandbookSection('permanent', 'Permanent', this.state.permanentHandbook, this.state.isPermanentLoading, this.state.isPermanentUploading, this.permanentFileRef)}
                        </div>
                    </div>

                    {/* Section Divider */}
                    <hr className='border-gray-200 my-[10px] mb-[30px]' />

                    {/* ==================== Section 2: CPD Images ==================== */}
                    <div>
                        <div className='flex items-center gap-2 mb-[15px]'>
                            <h1 className='text-[20px] m-0 font-semibold text-[#1e293b]'>CPD Images</h1>
                        </div>

                        {/* Upload CPD Images */}
                        <div className='upload-section p-5 bg-white rounded-[16px] mb-[20px]' style={{ border: "1px solid #DFDFDF" }}>
                            <h2 className='text-[16px] font-semibold mt-0 mb-4'>Upload New Images</h2>
                            <div className='upload-area'>
                                <div className='flex items-center gap-4 mb-4'>
                                    <label className='flex items-center gap-[8px] py-[10px] px-[20px] text-[14px] font-medium rounded-[8px] text-white bg-[#004AAD] hover:bg-[#003080] cursor-pointer'>
                                        <FontAwesomeIcon icon={faUpload} /> Select Images
                                        <input type='file' accept='image/jpeg,image/jpg,image/png,image/gif' multiple onChange={this.handleFileSelect} ref={this.fileInputRef} className='hidden' />
                                    </label>
                                    <span className='text-gray-500 text-sm'>Allowed: JPG, PNG, GIF (Max 2MB)</span>
                                    {this.state.isUploading && <ProgressSpinner style={{ width: '24px', height: '24px' }} strokeWidth="4" />}
                                </div>

                                {this.state.tempImages.length > 0 && (
                                    <div className='temp-images-section'>
                                        <h3 className='text-[14px] font-medium mb-3 text-gray-700'>Images to save ({this.state.tempImages.length})</h3>
                                        <div className='grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4 mb-4'>
                                            {this.state.tempImages.map((img, index) => (
                                                <div key={index} className='relative group'>
                                                    <img src={img.preview} alt={img.originalName} className='w-full h-[120px] object-cover rounded-[8px] border border-gray-200' />
                                                    <button onClick={() => this.removeTempImage(index)} className='absolute top-1 right-1 w-6 h-6 bg-red-500 text-white rounded-full text-xs opacity-0 group-hover:opacity-100 transition-opacity'>
                                                        <FontAwesomeIcon icon={faTrash} />
                                                    </button>
                                                </div>
                                            ))}
                                        </div>
                                        <button onClick={this.saveImages} disabled={this.state.isSaving} className='flex items-center gap-[5px] py-[10px] px-[20px] text-[14px] font-medium rounded-[8px] text-white bg-[#0077B6] hover:bg-[#005f92] border-0 disabled:opacity-50'>
                                            {this.state.isSaving ? <><ProgressSpinner style={{ width: '16px', height: '16px' }} strokeWidth="4" /> Saving...</> : <><FontAwesomeIcon icon={faAdd} /> Add Images</>}
                                        </button>
                                    </div>
                                )}
                            </div>
                        </div>

                        {/* Saved CPD Images */}
                        <div className='saved-images-section p-5 bg-white rounded-[16px]' style={{ border: "1px solid #DFDFDF" }}>
                            <div className='flex items-center justify-between mb-4'>
                                <h2 className='text-[16px] font-semibold m-0'>Saved CPD Images ({this.state.savedImages.length})</h2>
                                {this.state.isReordering && <span className='text-sm text-blue-600'>Saving order...</span>}
                            </div>
                            <p className='text-sm text-gray-500 mb-4'>Drag and drop to reorder images</p>

                            {this.state.isLoading ? (
                                <div className='flex justify-center items-center py-10'>
                                    <ProgressSpinner style={{ width: '40px', height: '40px' }} strokeWidth="4" />
                                </div>
                            ) : this.state.savedImages.length === 0 ? (
                                <div className='text-center py-10 text-gray-500'>
                                    <FontAwesomeIcon icon={faImage} className='text-4xl mb-3' />
                                    <p>No images uploaded yet</p>
                                </div>
                            ) : (
                                <div className='flex flex-wrap gap-4'>
                                    {this.state.savedImages.map((img, index) => (
                                        <div
                                            key={img.filename}
                                            draggable
                                            onDragStart={(e) => this.handleDragStart(e, index)}
                                            onDragOver={(e) => this.handleDragOver(e, index)}
                                            onDragEnd={this.handleDragEnd}
                                            className={`image-card w-[200px] border border-gray-200 rounded-[12px] overflow-hidden cursor-move ${this.state.draggedIndex === index ? 'opacity-50 border-blue-500 border-2' : ''}`}
                                        >
                                            <div className='relative'>
                                                <div className='absolute top-2 left-2 bg-white/80 rounded px-2 py-1 text-xs font-medium text-gray-600'>
                                                    <FontAwesomeIcon icon={faGripVertical} className='mr-1' /> {index + 1}
                                                </div>
                                                <img
                                                    src={`${APIURL.replace(/\/$/, '')}${img.path}`}
                                                    alt={img.filename}
                                                    className='w-full h-[150px] object-cover'
                                                    onError={(e) => { e.target.src = 'data:image/svg+xml,<svg xmlns="http://www.w3.org/2000/svg" width="100" height="100"><rect fill="%23f0f0f0" width="100" height="100"/><text fill="%23999" x="50%" y="50%" dominant-baseline="middle" text-anchor="middle">Error</text></svg>'; }}
                                                />
                                                {this.state.updateImageId === img.filename && (
                                                    <div className='absolute inset-0 bg-black bg-opacity-50 flex items-center justify-center'>
                                                        <ProgressSpinner style={{ width: '30px', height: '30px' }} strokeWidth="4" />
                                                    </div>
                                                )}
                                            </div>
                                            <div className='p-2 flex gap-2'>
                                                <label className='flex-1 py-[6px] px-[10px] text-[12px] font-medium rounded-[6px] text-[#0077B6] bg-cyan-50 hover:bg-cyan-100 border-0 text-center cursor-pointer'>
                                                    Update
                                                    <input type='file' accept='image/jpeg,image/jpg,image/png,image/gif' onChange={(e) => this.handleUpdateFileSelect(e, img.filename)} className='hidden' />
                                                </label>
                                                <button onClick={() => this.deleteImage(img.filename)} className='py-[6px] px-[10px] text-[12px] font-medium rounded-[6px] text-red-600 bg-red-50 hover:bg-red-100 border-0'>
                                                    <FontAwesomeIcon icon={faTrash} />
                                                </button>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            </div>
        );
    }
}
